import React, { useCallback, useEffect, useState } from 'react';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import { useSnackbar } from 'notistack';
import { RViewer, RViewerTrigger } from 'components/atoms/ImageViewer/Index';
import { fetchAsset } from '../../../api/asset';
import Spin from '../../atoms/Spin/Spin';

interface Props {
    photos: { id: number; desc?: string }[];
    options?: any;
}

const ImageGallery = ({ photos, options }: Props) => {
    const classes = useStyles();
    const [photoUrls, setPhotoUrls] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const { enqueueSnackbar } = useSnackbar();

    const load = useCallback(async () => {
        try {
            const list = photos.map(async (p) => {
                const data = await fetchAsset(p.id);
                return {
                    id: p.id,
                    desc: p.desc,
                    data,
                };
            });
            const promises = list.map(async (l) => {
                const item = await l;
                const url = `data:image/png;base64,${item.data}`;
                return {
                    id: item.id,
                    desc: item.desc,
                    url,
                };
            });
            const items = await Promise.all(promises);

            setPhotoUrls(items);
        } catch (e) {
            enqueueSnackbar(`Ошибка при получении данных: ${e.response?.data?.message || e}`, {
                variant: 'error',
            });
        } finally {
            setLoading(false);
        }
    }, [enqueueSnackbar, photos]);

    useEffect(() => {
        let mount = true;
        if (mount) {
            load();
        }

        return () => {
            mount = false;
        };
    }, []);

    return (
        <RViewer
            imageUrls={photoUrls.map((p) => p.url)}
            options={options ? defaultOptions : { ...defaultOptions, ...options }}
        >
            <figure className={classes.images}>
                {loading && <Spin />}
                {!loading &&
                    photoUrls.map((pic, index) => (
                        <RViewerTrigger index={index} key={index}>
                            <div className={classes.images}>
                                <div className={classes.imageWrapper}>
                                    <div className={classes.image}>
                                        <img src={pic.url} alt={pic.desc} />
                                        <div className={classes.imageAlt}>{pic.desc}</div>
                                    </div>
                                </div>
                            </div>
                        </RViewerTrigger>
                    ))}
            </figure>
        </RViewer>
    );
};

const useStyles = makeStyles(() =>
    createStyles({
        images: {
            padding: 0,
            margin: 0,
            display: 'flex',
            justifyContent: 'flex-start',
            alignContent: 'center',
            alignItems: 'center',
            flexWrap: 'wrap',
            position: 'relative',
        },
        imageWrapper: {
            display: 'inline-block',
            position: 'relative',
            width: '250px',
            height: '250px',
            overflow: 'hidden',
            margin: '5px',
            transition: '0.3s',

            '&:hover': {
                boxShadow: '0px 0px 12px 0px rgba(0, 0, 0, 0.9)',
            },
        },
        image: {
            width: '100%',
            height: '100%',
            cursor: 'pointer',
            display: 'inline-block',

            '& img': {
                width: '100%',
                height: '100%',
            },
        },
        imageAlt: {
            position: 'absolute',
            bottom: '0',
            left: '0',
            height: '35px',
            textAlign: 'center',
            background: 'rgba(0, 0, 0, 0.6)',
            width: '100%',
            padding: '2px',
            color: '#fff',
            borderBottom: ' 1px solid rgb(171, 171, 171)',
            borderBight: ' 1px solid rgb(171, 171, 171)',
            borderLeft: ' 1px solid rgb(171, 171, 171)',
            fontSize: ' 14px',
        },
    })
);

const defaultOptions = {
    inline: false,
    button: true,
    navbar: true,
    title: (image: any) => image.alt,
    toolbar: {
        zoomIn: {
            show: true,
            size: 'large',
        },
        zoomOut: {
            show: true,
            size: 'large',
        },
        oneToOne: {
            show: true,
            size: 'large',
        },
        reset: {
            show: true,
            size: 'large',
        },
        prev: {
            show: true,
            size: 'large',
        },
        play: {
            show: true,
            size: 'large',
        },
        next: {
            show: true,
            size: 'large',
        },
        rotateLeft: {
            show: true,
            size: 'large',
        },
        rotateRight: {
            show: true,
            size: 'large',
        },
        flipHorizontal: {
            show: true,
            size: 'large',
        },
        flipVertical: {
            show: true,
            size: 'large',
        },
    },
};

export default ImageGallery;
